import {
  Badge,
  FlexBox,
  Icon,
  Page,
  TabItem,
  Tabs,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { Queue } from 'lib-shared-legacy-types';
import {
  ampli,
  SettingsQueueAddedProperties,
  SettingsQueueSavedProperties,
} from 'lib-shared-tracking';
import isUndefined from 'lodash/isUndefined';
import pickBy from 'lodash/pickBy';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { AppLink } from 'components/appLink';
import { withQueues, WithQueuesInjectedProps } from 'hocs/queues';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { diff } from 'utils/deepObjectDiff';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { QueuesSettingsForm } from '../../components/queuesSettingsForm';
import type { FormValues as QueuesSettingsFormValues } from '../../components/queuesSettingsForm/types/settings';
import { QueuesAgents } from '../../containers/queuesAgents';
import { QueuesAlertsForm } from '../../containers/queuesAlertsForm';
import { QueuesTeams } from '../../containers/queuesTeams';
import { QueuesAlertsFormValues } from '../queuesAlertsForm';
import {
  createAlertsPayload,
  createPayload,
  EMPTY_CHANNEL_TIMEOUT,
} from '../utils';

type QueuesDetailsPageProps = {
  mode: 'create' | 'edit';
  currentQueue?: Queue;
} & WithQueuesInjectedProps;

export const QueuesDetailsPage = withQueues(
  (props: QueuesDetailsPageProps): JSX.Element => {
    const navigate = useNavigate();
    const params = useParams<'id'>();
    const notify = useNotifications();
    const flags = useFeatureFlag(['teams-assigning-teams-to-queues']);

    const hasQueueOfferingAccess = useHasAccess('settings.queue.offering');
    const hasQueueMembersAccess = useHasAccess('settings.queue.members');

    useMount(() => {
      const { id } = params;

      if (id) {
        props.queues.getQueue(id);
      }
    });

    const isEditMode = props.mode === 'edit';

    const initFormValues = (): QueuesSettingsFormValues => {
      const { queues } = props;
      const { item } = queues;

      if (!item) {
        return {
          name: '',
          priority: null,
          is_default: false,
          is_archived: false,
          offer_timeout: null,
          wrapup_timeout: null,
          offer_algorithm: 'one_at_a_time_random',
          is_do_not_offer_enabled: false,
          do_not_offer_timeouts: EMPTY_CHANNEL_TIMEOUT,
          offer_abandoned_conversations: false,
          is_preferred_agent_enabled: false,
          is_personal_offer_prioritized: false,
          preferred_agent_timeouts: EMPTY_CHANNEL_TIMEOUT,
          preferred_agent_offline_timeout_enabled: false,
          preferred_agent_offline_timeout: 0,
          personal_agent_offline_timeout_enabled: false,
          personal_agent_offline_timeout: 0,
        };
      }

      return {
        ...item,
        do_not_offer_timeouts:
          item.do_not_offer_timeouts || EMPTY_CHANNEL_TIMEOUT,
        offer_abandoned_conversations: item.offer_abandoned_conversations,
        preferred_agent_timeouts:
          item.preferred_agent_timeouts || EMPTY_CHANNEL_TIMEOUT,
        preferred_agent_offline_timeout_enabled:
          item.preferred_agent_offline_timeout !== null,
        preferred_agent_offline_timeout:
          item.preferred_agent_offline_timeout || 0,
        personal_agent_offline_timeout_enabled:
          item.personal_agent_offline_timeout !== null,
        personal_agent_offline_timeout:
          item.personal_agent_offline_timeout || 0,
      };
    };

    const handleQueueSettingsAnalytics = (
      values: QueuesSettingsFormValues,
    ): void => {
      const {
        do_not_offer_timeouts: lastAgentWaitingTime,
        is_default: isDefault,
        is_archived: isArchived,
        is_do_not_offer_enabled: lastAgentActive,
        is_personal_offer_prioritized: isPersonalOfferPrioritized,
        is_preferred_agent_enabled: preferredAgentActive,
        offer_algorithm: offerAlgorithm,
        offer_abandoned_conversations: offerAbandonedConversation,
        offer_timeout: offerTimeout,
        personal_agent_offline_timeout_enabled: lastAgentOffline,
        preferred_agent_timeouts: preferredAgentWaitingTime,
        preferred_agent_offline_timeout_enabled: preferredAgentOffline,
        priority: priorityLevel,
        wrapup_timeout: wrapUpTimeout,
      } = diff(
        isEditMode ? initFormValues() : {},
        values,
      ) as Partial<QueuesSettingsFormValues>;

      const properties:
        | SettingsQueueAddedProperties
        | SettingsQueueSavedProperties = pickBy(
        {
          // General
          priorityLevel,
          isDefault,
          isArchived,
          // Offer settings
          offerAlgorithm,
          offerTimeout,
          wrapUpTimeout,
          offerAbandonedConversation,
          // Last Agent
          lastAgentActive,
          isPersonalOfferPrioritized,
          lastAgentWaitingTimePhone: lastAgentWaitingTime?.pstn_phone,
          lastAgentWaitingTimeEmail: lastAgentWaitingTime?.email,
          lastAgentWaitingTimeChat: lastAgentWaitingTime?.widget_chat,
          lastAgentWaitingTimeTexts: lastAgentWaitingTime?.facebook_messenger,
          lastAgentOffline,
          // Preferred Agent
          preferredAgentActive,
          preferredAgentWaitingTimePhone: preferredAgentWaitingTime?.pstn_phone,
          preferredAgentWaitingTimeEmail: preferredAgentWaitingTime?.email,
          preferredAgentWaitingTimeChat: preferredAgentWaitingTime?.widget_chat,
          preferredAgentWaitingTimeTexts:
            preferredAgentWaitingTime?.facebook_messenger,
          preferredAgentOffline,
        },
        (type) => !isUndefined(type),
      );

      isEditMode
        ? ampli.settingsQueueSaved(properties as SettingsQueueSavedProperties)
        : ampli.settingsQueueAdded(properties as SettingsQueueAddedProperties);
    };

    const handleQueuesSettingsFormSubmit = (
      values: QueuesSettingsFormValues,
    ): void => {
      const { queues } = props;
      const { createQueue, updateQueue, item, getQueue } = queues;

      (isEditMode && item
        ? updateQueue(item.id, createPayload(values, item))
        : createQueue(createPayload(values))
      )
        .then((r) => {
          notify(
            {
              title: `Queue ${isEditMode ? 'updated' : 'created'}`,
              text: `Queue "${values.name}" ${
                isEditMode ? 'updated' : 'created'
              }`,
              time: 5000,
              icon: 'done',
            },
            NotificationType.Success,
          );
          handleQueueSettingsAnalytics(values);
          navigate(`/settings/queues/${r.data.id}`);
          getQueue(r.data.id);
        })
        .catch((_e: Error) => {
          notify(
            {
              title: 'An error occurred',
              text: `An error occurred when trying to ${
                isEditMode ? 'update' : 'create'
              } the "${values.name}" queue`,
              icon: 'exclamation',
            },
            NotificationType.Error,
          );
        });
    };

    const initAlertsFormValues = (): QueuesAlertsFormValues => {
      const { queues } = props;
      const { item } = queues;

      if (item && item.queue_thresholds) {
        return {
          is_sla_percentage: !!item.queue_thresholds.sla_percentage,
          sla_percentage: item.queue_thresholds.sla_percentage || 80,
          sla_time_limit: item.queue_thresholds.sla_time_limit,
          is_longest_wait: !!item.queue_thresholds?.longest_wait,
          longest_wait: item.queue_thresholds.longest_wait || 20,
          is_available_agents: !!item.queue_thresholds.available_agents,
          available_agents: item.queue_thresholds.available_agents,
          is_waiting_conversations:
            !!item.queue_thresholds.waiting_conversations,
          waiting_conversations:
            item.queue_thresholds?.waiting_conversations || 10,
        };
      }

      return {
        is_sla_percentage: false,
        sla_percentage: 0,
        sla_time_limit: 20,
        is_longest_wait: false,
        longest_wait: 0,
        is_available_agents: false,
        available_agents: 0,
        is_waiting_conversations: false,
        waiting_conversations: 0,
      };
    };

    const handleQueueAlertsAnalytics = (
      values: QueuesAlertsFormValues,
    ): void => {
      const {
        is_sla_percentage: serviceLevelAlertEnabled,
        sla_percentage: serviceLevelPercentage,
        sla_time_limit: serviceLevelTimeLimit,
        is_longest_wait: currentLongestWaitEnabled,
        longest_wait: currentLongestWait,
        is_available_agents: availableAgentsEnabled,
        available_agents: availableAgentsLimit,
        is_waiting_conversations: maximumWaitingEnabled,
        waiting_conversations: maximumWaiting,
      } = diff(
        initAlertsFormValues(),
        values,
      ) as Partial<QueuesAlertsFormValues>;

      ampli.settingsQueueAlertsSaved(
        pickBy(
          {
            serviceLevelAlertEnabled,
            serviceLevelPercentage,
            serviceLevelTimeLimit,
            currentLongestWaitEnabled,
            currentLongestWait,
            availableAgentsEnabled,
            availableAgentsLimit,
            maximumWaitingEnabled,
            maximumWaiting,
          },
          (type) => !isUndefined(type),
        ),
      );
    };

    const handleQueuesAlertsFormSubmit = (
      values: QueuesAlertsFormValues,
    ): void => {
      const { queues } = props;
      const { updateQueue, item, getQueue } = queues;

      if (item) {
        updateQueue(item.id, createAlertsPayload(values, item))
          .then((r) => {
            notify(
              {
                title: 'Queue updated',
                text: `Queue "${item.name}" updated`,
                icon: 'done',
                time: 5000,
              },
              NotificationType.Success,
            );
            handleQueueAlertsAnalytics(values);
            getQueue(r.data.id);
          })
          .catch((_e: Error) => {
            notify(
              {
                title: 'An error occurred',
                text: `An error occurred when trying to update the "${item.name}" queue`,
                icon: 'exclamation',
              },
              NotificationType.Error,
            );
          });
      }
    };

    const { queues } = props;
    const { id: queueId } = params;
    const { item: queue, createAsyncStatus, updateAsyncStatus } = queues;

    const tabsDefinition: TabItem[] = [
      {
        id: 'queueSettings',
        label: 'Queue settings',
        disabled: isEditMode ? !hasQueueOfferingAccess : false,
      },
      {
        id: 'alertsTab',
        label: 'Alerts',
        disabled: isEditMode ? !hasQueueOfferingAccess : true,
      },
      {
        id: 'agentsTab',
        label: 'Agents',
        disabled: isEditMode ? !hasQueueMembersAccess : true,
      },
    ];

    if (flags.teamsAssigningTeamsToQueues) {
      tabsDefinition.push({
        id: 'teamsTab',
        label: 'Teams',
        disabled: isEditMode ? !hasQueueMembersAccess : true,
      });
    }

    const [activeTab, setActiveTab] = useState(
      tabsDefinition.find((tab) => !tab.disabled)?.id,
    );

    return (
      <Page>
        <Page.Head
          title={
            <FlexBox gap="200" alignItems="center">
              <AppLink path="/settings/queues" style={{ color: 'inherit' }}>
                Queues
              </AppLink>

              <Icon name="angle-right" />

              {queue ? queue.name : 'Create queue'}

              {queue?.is_archived && (
                <Badge variant="error" size="medium">
                  Archived
                </Badge>
              )}
            </FlexBox>
          }
        />

        <Page.Tabs>
          <Tabs
            selectedId={activeTab}
            tabs={tabsDefinition}
            onChange={(tab) => setActiveTab(tab)}
          />
        </Page.Tabs>

        <Page.Body>
          {activeTab == 'queueSettings' && (
            <QueuesSettingsForm
              onSubmit={handleQueuesSettingsFormSubmit}
              mode={isEditMode ? 'edit' : 'create'}
              initialValues={initFormValues()}
              isLoading={
                createAsyncStatus === 'loading' ||
                updateAsyncStatus === 'loading'
              }
            />
          )}

          {activeTab === 'alertsTab' && (
            <QueuesAlertsForm
              onSubmit={handleQueuesAlertsFormSubmit}
              initialValues={initAlertsFormValues()}
              isLoading={
                createAsyncStatus === 'loading' ||
                updateAsyncStatus === 'loading'
              }
            />
          )}

          {activeTab === 'agentsTab' && (
            <>
              {queue !== null && queueId && (
                <QueuesAgents
                  queueId={queueId}
                  queueOfferAlgorithm={queue.offer_algorithm}
                  queueName={queue.name}
                />
              )}
            </>
          )}

          {activeTab === 'teamsTab' && (
            <>
              {queue !== null && queueId && (
                <QueuesTeams
                  queueId={queueId}
                  queueOfferAlgorithm={queue.offer_algorithm}
                  queueName={queue.name}
                />
              )}
            </>
          )}
        </Page.Body>
      </Page>
    );
  },
);
